import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

interface LoadingScreenProps {
  /**
   * Fill the viewport — app boot and the route guards that replace the whole
   * shell. Pass `false` when the loader renders *inside* the app's page card so
   * it does not force that card to scroll.
   */
  fullScreen?: boolean;
}

/**
 * The shared "loading" state.
 *
 * Painted on `bg-card`, not `bg-background`: `--background` is the gray gutter
 * the floating page card sits on, so a full-bleed loader in that color flashed
 * gray and then snapped to the (white) page surface. `bg-card` is the surface
 * every page actually renders on — white in light mode, the card gray in dark —
 * so the boot screen and the page it resolves into read as one continuous
 * surface, and a nested loader blends into the card containing it.
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({ fullScreen = true }) => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className={cn(
        'flex w-full items-center justify-center bg-card text-foreground',
        fullScreen ? 'min-h-screen' : 'min-h-[50vh]',
      )}
    >
      <div className="flex flex-col items-center gap-4">
        <span
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground"
          aria-hidden
        />
        <p className="text-sm font-medium text-muted-foreground">{t('loading')}</p>
      </div>
    </div>
  );
};
